import React, { useState, useEffect, useRef } from 'react';
import { StickyNote, Download, Power, LogOut, ExternalLink, MonitorSmartphone, Wrench, ChevronDown, LifeBuoy } from 'lucide-react';
import { api } from '../api';
import { toast } from 'sonner';

function ToolsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const connectDesk = async () => {
    setOpen(false);
    try {
      const r = await api.get('/sso/desk-link');
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not connect to Desk');
    }
  };

  const connectSupport = async () => {
    setOpen(false);
    try {
      const r = await api.get('/sso/support-link');
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not connect to Support');
    }
  };

  const openWpBoard = async () => {
    setOpen(false);
    try {
      const r = await api.post('/sso/wpboard');
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open WP Board');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="tools-menu-button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3 py-1.5 text-xs font-bold hover:bg-slate-700 transition-colors"
        title="Connected tools"
      >
        <Wrench size={13} /> <span className="hidden sm:inline">Tools</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div data-testid="tools-menu" className="absolute right-0 top-full mt-2 w-52 bg-white border-2 border-slate-200 rounded-xl sticky-shadow py-1.5 z-[60]">
          <button data-testid="tools-connect-desk" onClick={connectDesk}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-slate-800 hover:bg-teal-50 transition-colors text-left">
            <span className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center"><MonitorSmartphone size={14} /></span>
            <span>Connect Desk<span className="block text-[9px] font-semibold text-slate-400">desk.gosnd.com · auto-login</span></span>
          </button>
          <button data-testid="tools-connect-support" onClick={connectSupport}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-slate-800 hover:bg-rose-50 transition-colors text-left">
            <span className="w-7 h-7 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center"><LifeBuoy size={14} /></span>
            <span>Support Desk<span className="block text-[9px] font-semibold text-slate-400">support.gosnd.com · auto-login</span></span>
          </button>
          <button data-testid="tools-wpboard" onClick={openWpBoard}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-slate-800 hover:bg-indigo-50 transition-colors text-left">
            <span className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center"><ExternalLink size={14} /></span>
            <span>WP Board<span className="block text-[9px] font-semibold text-slate-400">wpboard.gosnd.com · auto-login</span></span>
          </button>
        </div>
      )}
    </div>
  );
}

const ROLE_BADGE = {
  admin: 'bg-blue-600 text-white',
  agent: 'bg-yellow-300 text-yellow-900',
  tv: 'bg-slate-900 text-amber-300',
  pending: 'bg-violet-200 text-violet-800',
};

export default function TopBar({ user, agent, toggleAvailability, onLogout }) {
  const [installEvt, setInstallEvt] = useState(null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const install = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FFFDF6]/90 backdrop-blur border-b-2 border-amber-200 px-4 md:px-8 py-3 flex items-center justify-between gap-3" data-testid="top-bar">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-yellow-300 border-2 border-yellow-400 rotate-[-4deg] flex items-center justify-center sticky-shadow">
          <StickyNote size={18} className="text-yellow-900" />
        </div>
        <div>
          <h1 className="font-display font-extrabold text-lg leading-none text-slate-900">SND CRM</h1>
          <p className="font-hand text-sm text-amber-700 leading-none mt-0.5 hidden sm:block">your artboard workspace</p>
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {installEvt && (
          <button
            data-testid="install-app-button"
            onClick={install}
            className="flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3 py-1.5 text-xs font-bold hover:bg-slate-700 transition-colors"
          >
            <Download size={13} /> <span className="hidden sm:inline">Install App</span>
          </button>
        )}
        {(user.role === 'agent' || user.role === 'admin') && <ToolsMenu />}
        {user.role === 'agent' && agent && (
          <button
            data-testid="availability-toggle"
            onClick={toggleAvailability}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold border-2 transition-colors ${(agent.available ?? true) ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100' : 'bg-red-500 border-red-500 text-white hover:bg-red-600'}`}
            title={(agent.available ?? true) ? 'Click to go offline (pause new leads)' : 'Click to go back online'}
          >
            <Power size={13} /> <span className="hidden md:inline">{(agent.available ?? true) ? 'Available' : 'Offline'}</span>
          </button>
        )}
        <div className="flex items-center gap-2 bg-white border-2 border-slate-200 rounded-full pl-3 pr-1 py-1" data-testid="user-chip">
          <span className="text-xs font-bold text-slate-800 max-w-[110px] sm:max-w-none truncate">{user.name || user.emp_id}</span>
          <span className={`text-[9px] font-extrabold uppercase tracking-wider rounded-full px-2 py-0.5 ${ROLE_BADGE[user.role] || ROLE_BADGE.pending}`} data-testid="user-role-badge">{user.role}</span>
          <button data-testid="logout-button" onClick={onLogout} title="Logout" aria-label="Logout"
            className="p-1.5 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </header>
  );
}
